import React, { useCallback, useMemo, useState } from 'react'

import { VStack } from 'govwell-ui'
import { SelectModeProps } from 'govwell-ui/components/Select/types'
import {
  EntityLayerFragment,
  EntityVersionWithFieldsFragment,
  SearchEntityVersionsByLocationFragment,
} from 'types/graphql'

import EntityVersionSearchMapCard from 'src/components/Selects/EntityVersionSearch/EntityVersionSearchMapCard'
import { buildEntityVersionSearchMapShapes } from 'src/components/Selects/EntityVersionSearch/entityVersionSearchMapShapes'
import { SelectValue } from 'src/components/Selects/EntityVersionSearch/types'
import {
  isSelectValueSearchEntityVersionFragment,
  selectValueEquals,
} from 'src/components/Selects/EntityVersionSearch/util'
import ArcGisMap from 'src/components/shared/ArcGisMap'
import { MapBoundaries } from 'src/components/shared/ArcGisMap/types'
import {
  useGetEntityVersionByIds,
  useSearchEntityVersionsByLocationQuery,
} from 'src/fetch/entityVersions'
import useDebounce from 'src/hooks/use-debounce'
import { filterNil } from 'src/utils'
import {
  EntityLayerTypes,
  getEntityLayerMapSwitcherLabel,
  sortEntityLayersForMapSwitcher,
} from 'src/utils/entityDisplayHelpers'

export type EntityVersionSearchMapProps = SelectModeProps<SelectValue> & {
  entityLayers: EntityLayerFragment[]
}

const EntityVersionSearchMap = (props: EntityVersionSearchMapProps) => {
  const { entityLayers, isMultiSelect, value, onValueChange } = props

  const selectedValues = useMemo(() => {
    if (isMultiSelect) {
      return value ?? []
    }
    return value ? [value] : []
  }, [isMultiSelect, value])

  // Used to display the selected values in reverse order
  const reversedSelectedValues = useMemo(() => {
    return selectedValues.slice().reverse()
  }, [selectedValues])

  const reversedEntityVersions = useMemo(() => {
    return reversedSelectedValues.filter((v) => isSelectValueSearchEntityVersionFragment(v))
  }, [reversedSelectedValues])

  const defaultSelectedLayer = useMemo(() => {
    if (!entityLayers.length) {
      return null
    }

    const lastSelectedEntityVersionWithLayer = reversedEntityVersions?.[0]
    const lastSelectedLayer = entityLayers.find(
      (l) => l.id === lastSelectedEntityVersionWithLayer?.entity?.layer?.id
    )

    if (lastSelectedLayer) {
      // If there are already configured entities with layers, use the last selected entity version with a layer
      return lastSelectedLayer
    }

    // Prefer address layer
    return entityLayers.find((l) => l.type === 'Address') ?? entityLayers[0] ?? null
  }, [entityLayers, reversedEntityVersions])

  const [selectedLayer, setSelectedLayer] = useState<EntityLayerFragment | null>(
    defaultSelectedLayer
  )
  const selectedLayerSingularLabel = selectedLayer
    ? getEntityLayerMapSwitcherLabel(selectedLayer).toLowerCase()
    : 'entity'

  const [mapBoundaries, setMapBoundaries] = useState<MapBoundaries | null>(null)
  const debouncedMapBoundaries = useDebounce(mapBoundaries)

  const searchByLocationInput = useMemo(() => {
    if (!selectedLayer?.id) {
      return null
    }
    return {
      entityLayerId: selectedLayer.id,
      ...(debouncedMapBoundaries
        ? { searchLocationInput: { boundingBox: debouncedMapBoundaries } }
        : {}),
      pageSize: 150,
    }
  }, [selectedLayer, debouncedMapBoundaries])

  const { entityVersions, isFetching } =
    useSearchEntityVersionsByLocationQuery(searchByLocationInput)

  const {
    entityVersions: selectedEntityVersionsWithFields,
    isFetching: isFetchingSelectedEntityVersionsWithFields,
  } = useGetEntityVersionByIds(reversedEntityVersions.map((v) => v.id))

  const selectedEntityVersionsWithFieldsMap = useMemo(() => {
    const byId: Record<number, EntityVersionWithFieldsFragment> = {}
    selectedEntityVersionsWithFields.forEach((ev) => {
      if (ev) {
        byId[ev.id] = ev
      }
    })
    return byId
  }, [selectedEntityVersionsWithFields])

  const handleAddEntityVersion = useCallback(
    (entityVersion: SearchEntityVersionsByLocationFragment) => {
      if (selectedValues.some((v) => selectValueEquals(v, entityVersion))) {
        // Do not add the entity version if it already exists
        return
      }

      if (isMultiSelect) {
        onValueChange([...selectedValues, entityVersion])
      } else {
        onValueChange(entityVersion)
      }
    },
    [isMultiSelect, selectedValues, onValueChange]
  )

  const handleRemoveEntityVersion = useCallback(
    (selectValue: SelectValue) => {
      if (isMultiSelect) {
        onValueChange(selectedValues.filter((v) => !selectValueEquals(v, selectValue)))
      } else {
        onValueChange(null)
      }
    },
    [selectedValues, isMultiSelect, onValueChange]
  )

  const defaultEntityLayerType =
    selectedLayer?.type ?? entityLayers[0]?.type ?? EntityLayerTypes.Address

  const shapes = useMemo(() => {
    if (!entityVersions) {
      return []
    }

    return buildEntityVersionSearchMapShapes({
      entityVersions: filterNil(entityVersions),
      selectedValues,
      selectedLayerSingularLabel,
      defaultEntityLayerType,
      onAddEntityVersion: handleAddEntityVersion,
      onRemoveEntityVersion: handleRemoveEntityVersion,
    })
  }, [
    defaultEntityLayerType,
    entityVersions,
    handleAddEntityVersion,
    handleRemoveEntityVersion,
    selectedLayerSingularLabel,
    selectedValues,
  ])

  const selectLayerProps = useMemo(() => {
    if (!entityLayers || entityLayers.length <= 1) {
      // No need to show the select layer option if there is 0 or 1 layer
      return undefined
    }

    const sortedEntityLayers = sortEntityLayersForMapSwitcher(entityLayers)

    return {
      layerOptions: sortedEntityLayers.map((entityLayer) => ({
        label: getEntityLayerMapSwitcherLabel(entityLayer),
        value: `${entityLayer.id}`,
      })),
      selectedLayerOption: `${selectedLayer?.id ?? 0}`,
      onSelectedLayerOptionChange: (selectedValue: string) => {
        const next =
          sortedEntityLayers.find((entityLayer) => entityLayer.id === Number(selectedValue)) ?? null
        setSelectedLayer(next)
      },
    }
  }, [entityLayers, selectedLayer?.id])

  const height = '450px'

  return (
    <VStack gap={4} justify="center">
      <ArcGisMap
        height={height}
        isLoading={isFetching}
        onMapBoundariesChange={setMapBoundaries}
        shapes={shapes}
        isUserLocationEnabled
        shouldZoomWhenShapesUpdated={false}
        selectLayerProps={selectLayerProps}
      />
      <VStack gap={2}>
        {reversedSelectedValues.length > 0 ? (
          reversedSelectedValues.map((v) => {
            const entityVersionWithFields = isSelectValueSearchEntityVersionFragment(v)
              ? (selectedEntityVersionsWithFieldsMap[v.id] ?? null)
              : null

            const isEntityVersionWithFieldsLoading =
              isFetchingSelectedEntityVersionsWithFields &&
              !entityVersionWithFields &&
              isSelectValueSearchEntityVersionFragment(v)

            return (
              <EntityVersionSearchMapCard
                key={isSelectValueSearchEntityVersionFragment(v) ? v.id : v}
                entityVersion={v}
                entityVersionWithFields={entityVersionWithFields}
                onRemove={handleRemoveEntityVersion}
                isEntityVersionWithFieldsLoading={isEntityVersionWithFieldsLoading}
                isCollapsible={isMultiSelect}
              />
            )
          })
        ) : (
          <EntityVersionSearchMapCard entityVersion="None selected" />
        )}
      </VStack>
    </VStack>
  )
}

export default React.memo(EntityVersionSearchMap)
